import React, { useEffect, useMemo, useRef, useState } from "react";
import { SelectInstallmentOption } from "../components/SelectInstallmentOption";
import { getAreaWorth, getInstallmentPeriods, getMarkup } from "./utils.ts";
import { Input } from "../../../Input";
import { RootState } from "../../../../../store";
import { connect } from "react-redux";
import {
    selectPricesByPeriodData,
    selectCommercePricesByPeriodData,
} from "../../../../../store/selectors.ts";
import { getAreas, getFlatViewTypes, getFloorRanges } from "../utils.ts";
import { InfoRow } from "../../../InfoRow";
import { MANUAL_AREA } from "../../constants";
import { Switcher } from "../../../Switcher";

type PricingType = "residential" | "commerce";

type OwnProps = {
    type?: PricingType;
    setShareData: (v: any) => void;
    housing?: any;
};

type StateProps = {
    pricesByPeriod: any;
};

const MATCAP_1 = 728_900;
const MATCAP_2 = 963_200;

type MatCapOptionId = "none" | "child1" | "child2" | "manual";

const hasMatCapOverride = (areaStr: string) => areaStr.includes("|m]");
const isMatCapAllowedForLayout = (globalMatCap: boolean, areaStr: string) => {
    const hasM = hasMatCapOverride(areaStr);
    return globalMatCap !== hasM;
};

const sectionTitleClass =
    "hidden text-[13px] font-semibold uppercase tracking-[0.12em] text-[#7f8794] md:block";
const surfaceClass =
    "px-0 py-1 md:rounded-[22px] md:border md:border-[#edf1f5] md:bg-white md:p-5";

export const _ByPeriodModern: React.FC<OwnProps & StateProps> = ({
    pricesByPeriod,
    setShareData,
    type = "residential",
    housing,
}) => {
    const typeLabel = type === "commerce" ? "тип помещения" : "тип квартиры";
    const selectTypeLabel = `выберите ${typeLabel}`;

    const [installmentPeriod, setInstallmentPeriod] = useState<any>(0);
    const [floorRange, setFloorRange] = useState<any>();
    const [flatViewType, setFlatViewType] = useState<any>();
    const [areaWorth, setAreaWorth] = useState<number>(0);
    const [firstPayment, setFirstPayment] = useState<number>(0);
    const [matCapOption, setMatCapOption] = useState<any>({
        name: "без маткапа",
        value: { id: "none" as MatCapOptionId },
    });
    const [matCapManualAmount, setMatCapManualAmount] = useState<number>(0);
    const [fullPrice, setFullPrice] = useState<number>(0);
    const [rest, setRest] = useState<number>(0);
    const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
    const [markup, setMarkup] = useState<number>(0);
    const [area, setArea] = useState<any>(0);
    const [markupAfterFirstPayment, setMarkupAfterFirstPayment] = useState(false);
    const previousAutoFirstPaymentRef = useRef<number | null>(null);

    const floorRanges = getFloorRanges(pricesByPeriod);
    const flatViewTypes = getFlatViewTypes(floorRange);
    const areas = getAreas(pricesByPeriod, flatViewType);
    const installmentPeriods = getInstallmentPeriods(floorRange);
    const isAreaSelected = Number(area?.value || 0) > 0;

    const isInstallment = installmentPeriod?.value?.percent !== "full";
    const isManualAreaEmpty =
        area?.name === MANUAL_AREA && String(area?.value ?? "").trim().length === 0;

    const requiredPayment = useMemo(
        () => Number(pricesByPeriod?.requiredPayment || 0),
        [pricesByPeriod]
    );

    const matCapGloballyPossible = Boolean(
        (pricesByPeriod as any)?.matCap ?? housing?.matCap
    );

    const selectedAreaRaw: string = useMemo(() => {
        if (!area?.value) return "";
        if (area?.name === MANUAL_AREA) return "";

        const areaValue = String(area.value).trim();

        const allAreas: string[] = [
            ...((pricesByPeriod as any)?.areas || []),
            ...((pricesByPeriod as any)?.areas0 || []),
            ...((pricesByPeriod as any)?.areas2 || []),
            ...((pricesByPeriod as any)?.areas3 || []),
        ].filter(Boolean);

        const match = allAreas.find((a) => String(a).includes(`|${areaValue}|`));
        return (match || "").trim();
    }, [area, pricesByPeriod]);

    const matCapAllowedForThisLayout = useMemo(() => {
        if (!area || area?.name === MANUAL_AREA) return matCapGloballyPossible;

        if (selectedAreaRaw) {
            return isMatCapAllowedForLayout(matCapGloballyPossible, selectedAreaRaw);
        }

        return matCapGloballyPossible;
    }, [area, matCapGloballyPossible, selectedAreaRaw]);

    const matCapOptions = useMemo(
        () => [
            { name: "без маткапа", value: { id: "none" as MatCapOptionId } },
            {
                name: `маткап на 1 ребёнка (${MATCAP_1.toLocaleString()} ₽)`,
                value: { id: "child1" as MatCapOptionId },
            },
            {
                name: `маткап на 2 детей (${MATCAP_2.toLocaleString()} ₽)`,
                value: { id: "child2" as MatCapOptionId },
            },
            { name: "маткап (вручную)", value: { id: "manual" as MatCapOptionId } },
        ],
        []
    );

    const matCapPresetAmount = useMemo(() => {
        const id: MatCapOptionId = matCapOption?.value?.id || "none";
        if (id === "child1") return MATCAP_1;
        if (id === "child2") return MATCAP_2;
        return 0;
    }, [matCapOption]);

    const matCapAmountRaw = useMemo(() => {
        const id: MatCapOptionId = matCapOption?.value?.id || "none";
        if (id === "manual") return Number(matCapManualAmount) || 0;
        return matCapPresetAmount;
    }, [matCapOption, matCapManualAmount, matCapPresetAmount]);

    const matCapAmount = useMemo(() => {
        if (!matCapAllowedForThisLayout) return 0;
        return Math.max(Number(matCapAmountRaw) || 0, 0);
    }, [matCapAmountRaw, matCapAllowedForThisLayout]);

    const isManualMatCap =
        (matCapOption?.value?.id as MatCapOptionId) === "manual";

    useEffect(() => {
        if (!matCapAllowedForThisLayout) {
            setMatCapOption({
                name: "без маткапа",
                value: { id: "none" as MatCapOptionId },
            });
            setMatCapManualAmount(0);
        }
    }, [matCapAllowedForThisLayout]);

    useEffect(() => {
        if (installmentPeriod) {
            setAreaWorth(getAreaWorth(floorRange, flatViewType, installmentPeriod));
        }
        setMonthlyPayment(0);
    }, [installmentPeriod, flatViewType, floorRange]);

    useEffect(() => {
        if (!isInstallment) {
            previousAutoFirstPaymentRef.current = null;
            return;
        }

        const areaVal = Number(area?.value || 0);
        if (areaVal > 0 && requiredPayment > 0) {
            const minFirst = Math.round(areaVal * requiredPayment);
            setFirstPayment((prev) => {
                const prevValue = Number(prev || 0);
                const prevAuto = previousAutoFirstPaymentRef.current;
                const shouldSync =
                    !prevValue ||
                    prevAuto === null ||
                    prevValue === prevAuto ||
                    prevValue < minFirst;

                previousAutoFirstPaymentRef.current = minFirst;
                return shouldSync ? minFirst : prevValue;
            });
            return;
        }

        previousAutoFirstPaymentRef.current = null;
    }, [area, requiredPayment, isInstallment]);

    useEffect(() => {
        const areaVal = Number(area?.value || 0);
        if (areaVal > 0 && areaWorth > 0) setFullPrice(areaVal * areaWorth);
        else setFullPrice(0);
    }, [area, areaWorth]);

    useEffect(() => {
        if (fullPrice > 0) {
            setMarkup(
                getMarkup(
                    fullPrice,
                    installmentPeriod,
                    firstPayment,
                    markupAfterFirstPayment
                )
            );
        } else {
            setMarkup(0);
        }
    }, [fullPrice, installmentPeriod, firstPayment, markupAfterFirstPayment]);

    useEffect(() => {
        if (fullPrice > 0) setRest(fullPrice + markup - (firstPayment || 0));
        else setRest(0);
    }, [fullPrice, markup, firstPayment]);

    const restAfterMatCap = useMemo(() => {
        if (!isInstallment) return 0;
        return Math.max((Number(rest) || 0) - (Number(matCapAmount) || 0), 0);
    }, [isInstallment, rest, matCapAmount]);

    useEffect(() => {
        if (isInstallment && fullPrice > 0 && installmentPeriod?.value?.period > 1) {
            const period = Number(installmentPeriod.value.period || 0);
            const m = (restAfterMatCap || 0) / period;
            setMonthlyPayment(Math.round(m));
        } else {
            setMonthlyPayment(0);
        }
    }, [isInstallment, fullPrice, restAfterMatCap, installmentPeriod]);

    const shareData = useMemo(
        () => ({
            floor: floorRange?.value?.floorRange,
            flatType: flatViewType?.value?.name,
            area: area?.value,
            installmentPeriod: installmentPeriod?.value?.period,
            installmentPercent: installmentPeriod?.value?.percent,
            areaWorth,
            firstPayment: parseInt(String(firstPayment || 0), 10),
            matCap: {
                selected: matCapOption?.value?.id,
                amount: matCapAmount,
                allowed: matCapAllowedForThisLayout,
            },
            fullPrice,
            rest,
            restAfterMatCap,
            monthlyPayment,
            markup,
            markupAfterFirstPayment,
        }),
        [
            floorRange,
            flatViewType,
            area,
            installmentPeriod,
            areaWorth,
            firstPayment,
            matCapOption,
            matCapAmount,
            matCapAllowedForThisLayout,
            fullPrice,
            rest,
            restAfterMatCap,
            monthlyPayment,
            markup,
            markupAfterFirstPayment,
        ]
    );

    useEffect(() => {
        setShareData(shareData);
    }, [shareData, setShareData]);

    useEffect(() => {
        setArea(null);
        setFullPrice(0);
        setMarkup(0);
        setFirstPayment(0);
        previousAutoFirstPaymentRef.current = null;

        setMatCapOption({
            name: "без маткапа",
            value: { id: "none" as MatCapOptionId },
        });
        setMatCapManualAmount(0);

        if (installmentPeriods) setInstallmentPeriod(installmentPeriods?.full);
    }, [floorRange, flatViewType]); // eslint-disable-line react-hooks/exhaustive-deps

    const formattedWithMarkup = useMemo(() => {
        if (markupAfterFirstPayment && firstPayment > 0) {
            const value = rest + firstPayment;
            return value > 0 ? value.toLocaleString() : "0";
        }
        const value = fullPrice + markup;
        return value > 0 ? value.toLocaleString() : "0";
    }, [markupAfterFirstPayment, firstPayment, rest, markup, fullPrice]);

    const summaryItems = [
        firstPayment > 0
            ? {
                label: "Первый взнос",
                value: `${(firstPayment || 0).toLocaleString()} ₽`,
                tone: "text-[#141b2d]",
            }
            : null,
        isInstallment
            ? {
                label: "Остаток",
                value: `${(rest || 0).toLocaleString()} ₽`,
                tone: "text-[#141b2d]",
            }
            : null,
        isInstallment && matCapAmount > 0
            ? {
                label: "После маткапа",
                value: `${(restAfterMatCap || 0).toLocaleString()} ₽`,
                tone: "text-[#0f766e]",
            }
            : null,
        isInstallment
            ? {
                label: "Ежемесячный платёж",
                value: `${(monthlyPayment || 0).toLocaleString()} ₽`,
                tone: "text-[#7c3aed]",
            }
            : null,
    ].filter(Boolean) as { label: string; value: string; tone: string }[];

    const showFinancialConditionsTitle = Boolean(floorRange && isAreaSelected);
    const showDetailsBlock = Boolean(
        (fullPrice || 0) > 0 ||
        (rest || 0) > 0 ||
        (monthlyPayment || 0) > 0 ||
        (markup || 0) > 0
    );

    return (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
            <div className="order-1 lg:order-1">
                <div className={`${surfaceClass} space-y-4`}>
                    <div className="space-y-2">
                        <div className={sectionTitleClass}>Параметры расчёта</div>
                        <div className="hidden max-w-[560px] text-[14px] leading-5 text-[#5e6673] md:block">
                            Выберите параметры объекта и срок, затем проверьте итоговую
                            стоимость и условия платежей.
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="col-span-2">
                            <SelectInstallmentOption
                                options={floorRanges}
                                label="выберите этажи"
                                placeholder="этажи"
                                selectedOption={floorRange}
                                setSelectedOption={setFloorRange}
                                variant="pricing-modern"
                            />
                        </div>

                        {floorRange && (
                            <>
                                <SelectInstallmentOption
                                    options={flatViewTypes}
                                    label={selectTypeLabel}
                                    placeholder={typeLabel}
                                    selectedOption={flatViewType}
                                    setSelectedOption={setFlatViewType}
                                    variant="pricing-modern"
                                />

                                <SelectInstallmentOption
                                    options={areas}
                                    label="выберите квадратуру"
                                    placeholder="квадратура"
                                    selectedOption={area}
                                    setSelectedOption={setArea}
                                    variant="pricing-modern"
                                />

                                {area && area.name === MANUAL_AREA && (
                                    <div className="col-span-2">
                                        <Input
                                            value={area.value}
                                            onChange={(value) =>
                                                setArea({ name: MANUAL_AREA, value })
                                            }
                                            label="введите квадратуру"
                                            error={isManualAreaEmpty}
                                            inputMode="decimal"
                                            normalizeDecimal
                                            containerClassName="input_container-highlight-fade"
                                            inputClassName="input_main-pricing-manual-area"
                                            variant="pricing-modern"
                                        />
                                    </div>
                                )}

                                <div className="col-span-2">
                                    <SelectInstallmentOption
                                        options={installmentPeriods}
                                        label="выберите срок рассрочки"
                                        placeholder="срок рассрочки"
                                        selectedOption={installmentPeriod}
                                        setSelectedOption={setInstallmentPeriod}
                                        disabled={!isAreaSelected}
                                        variant="pricing-modern"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="border-t border-[#edf1f5] pt-4">
                        {showFinancialConditionsTitle && (
                            <div className={`${sectionTitleClass} mb-3`}>
                                Финансовые условия
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {floorRange && isAreaSelected && (
                                <div className="col-span-2">
                                    <Input
                                        name="areaWorth"
                                        type="number"
                                        placeholder="0 ₽"
                                        value={areaWorth || 0}
                                        onChange={(v: number) => setAreaWorth(Number(v) || 0)}
                                        label="стоимость квадрата:"
                                        variant="pricing-modern"
                                    />
                                </div>
                            )}

                            {isInstallment && isAreaSelected && (
                                <div className="col-span-2">
                                    <Input
                                        name="firstPayment"
                                        type="number"
                                        placeholder="0 ₽"
                                        value={firstPayment || 0}
                                        onChange={(v: number) =>
                                            setFirstPayment(Number(v) || 0)
                                        }
                                        label={`первоначальный взнос${
                                            requiredPayment
                                                ? ` (мин. ${requiredPayment} ₽/м²)`
                                                : ""
                                        }:`}
                                        variant="pricing-modern"
                                    />
                                </div>
                            )}
                        </div>

                        {isInstallment && isAreaSelected && (
                            <div className="mt-4 space-y-4">
                                <SelectInstallmentOption
                                    options={matCapOptions}
                                    label="маткап (будет вычтен из остатка):"
                                    selectedOption={matCapOption}
                                    setSelectedOption={setMatCapOption}
                                    disabled={!matCapAllowedForThisLayout}
                                    variant="pricing-modern"
                                />

                                {!matCapAllowedForThisLayout && (
                                    <div className="text-[12px] leading-5 text-[#7a828e]">
                                        Для выбранной планировки маткап недоступен.
                                    </div>
                                )}

                                {matCapAllowedForThisLayout && isManualMatCap && (
                                    <Input
                                        name="matCapManualAmount"
                                        type="number"
                                        placeholder="0 ₽"
                                        value={Number(matCapManualAmount) || 0}
                                        onChange={(v: number) =>
                                            setMatCapManualAmount(Number(v) || 0)
                                        }
                                        label="маткап (вручную, сколько осталось):"
                                        variant="pricing-modern"
                                    />
                                )}

                                <div className="pt-1">
                                    <Switcher
                                        checked={markupAfterFirstPayment}
                                        onChange={() =>
                                            setMarkupAfterFirstPayment(!markupAfterFirstPayment)
                                        }
                                        label="Прибавлять наценку после взноса"
                                        disabled={firstPayment < 1}
                                    />
                                    <div className="mt-2 text-[12px] leading-5 text-[#6f7682]">
                                        <div>
                                            Выкл: наценка начисляется на полную стоимость.
                                        </div>
                                        <div>
                                            Вкл: наценка начисляется только на остаток после
                                            первого взноса.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="order-2 space-y-4 lg:order-2">
                <div className={`${surfaceClass} rounded-[18px] bg-white p-4 lg:sticky lg:top-24 md:rounded-[22px] md:bg-white md:p-5`}>
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#7f8794] md:hidden">
                                Полная стоимость
                            </div>
                            <div className="mt-1 text-[28px] font-semibold leading-none text-[#101828] md:mt-2 md:text-[24px]">
                                {(fullPrice || 0).toLocaleString()} ₽
                            </div>
                            <div className="mt-2 hidden text-[14px] leading-5 text-[#667085] md:block">
                                Итог пересчитывается автоматически при изменении параметров.
                            </div>
                        </div>
                        {isInstallment && (
                            <div className="rounded-full border border-[#ddd6fe] bg-[#f5f3ff] px-3 py-2 text-[12px] font-medium text-[#6d28d9]">
                                {installmentPeriod?.value?.period
                                    ? `${installmentPeriod.value.period} мес.`
                                    : "Рассрочка"}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                        {summaryItems.map((item) => (
                            <div
                                key={item.label}
                                className="rounded-[14px] border border-[#edf1f5] p-3 md:rounded-[18px] md:p-4"
                            >
                                <div className="text-[12px] uppercase tracking-[0.08em] text-[#8a93a1]">
                                    {item.label}
                                </div>
                                <div className={`mt-2 text-[20px] font-semibold ${item.tone}`}>
                                    {item.value}
                                </div>
                            </div>
                        ))}
                    </div>

                    {showDetailsBlock && (
                        <div className="mt-5 hidden border-t border-[#edf1f5] pt-4 md:mt-5 md:block md:rounded-[18px] md:border md:border-[#edf1f5] md:p-4 md:pt-4">
                            <div className={`${sectionTitleClass} mb-2`}>Детализация</div>
                            <div className="space-y-1">
                                <InfoRow
                                    title="стоимость"
                                    value={(fullPrice || 0).toLocaleString()}
                                    unit="₽"
                                />
                                <InfoRow
                                    title="стоимость с наценкой"
                                    value={formattedWithMarkup}
                                    unit="₽"
                                />

                                {firstPayment > 0 && (
                                    <InfoRow
                                        title="остаток"
                                        value={(rest || 0).toLocaleString()}
                                        unit="₽"
                                    />
                                )}

                                {isInstallment && (
                                    <>
                                        {matCapAmount > 0 && (
                                            <InfoRow
                                                title="остаток (после маткапа)"
                                                value={(restAfterMatCap || 0).toLocaleString()}
                                                unit="₽"
                                            />
                                        )}

                                        <InfoRow
                                            title="ежемесячный платеж"
                                            value={(monthlyPayment || 0).toLocaleString()}
                                            unit="₽"
                                        />

                                        <InfoRow
                                            title="наценка"
                                            value={(markup || 0).toLocaleString()}
                                            unit="₽"
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
    const t: PricingType = ownProps.type ?? "residential";
    return {
        pricesByPeriod:
            t === "commerce"
                ? selectCommercePricesByPeriodData(state)
                : selectPricesByPeriodData(state),
    };
};

export const ByPeriodModern = connect(
    mapStateToProps,
    null
)(_ByPeriodModern);
